import {
  LoadingOutlined,
  PlusCircleFilled,
  ReloadOutlined,
} from "@ant-design/icons";
import { Alert, Form, Button, Select, Card, Row, Col } from "antd";
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  AddAttributes,
  GetAllAttributes,
  GetAllTermsAttributes,
} from "../../api/api";
import { create } from "zustand";

const { Option } = Select;

export const useAttributesStore = create((set) => ({
  list: [],
  groups: {},
  selected: [],
  options: {},
  data: {},
  setList: (list) => set({ list }),
  setData: (data) => set({ data }),
  setSelected: (selected) => set({ selected }),
  setOptions: (code, options) =>
    set((state) => ({ options: { ...state.options, [code]: options } })),
  setGroups: (groups) =>
    set((state) => ({ groups: { ...state.groups, ...groups } })),
  reset: () =>
    set({ list: [], groups: {}, selected: [], options: {}, data: {} }),
}));

export const Atributes = ({
  next,
  setcontrolStatusGeneral,
  controlStatusGeneral,
}) => {
  const attributes = useAttributesStore();
  const [form] = Form.useForm();
  const [loading, setloading] = useState(false);
  const [isLoadingAttributes, setIsLoadingAttributes] = useState(true);
  const [errorLoadingAttributes, setErrorLoadingAttributes] = useState(null);

  // Función para cargar los datos de los atributos
  const fetchAttributes = async (force = false) => {
    try {
      const response = await GetAllAttributes(force);
      if (!Array.isArray(response.data)) {
        throw new Error(
          response.data?.message || response.problem || "Respuesta inválida de WooCommerce"
        );
      }
      const formattedList = response.data.map((attr) => ({
        id: attr.id,
        name: attr.name,
      }));

      // Cargar los términos para cada atributo
      const termsPromises = formattedList.map(async (attr) => {
        try {
          const termsResponse = await GetAllTermsAttributes(attr.id, force);
          return {
            id: attr.id,
            terms: termsResponse.data || [],
          };
        } catch (error) {
          console.error(`Error al cargar términos para ${attr.name}:`, error);
          return {
            id: attr.id,
            terms: [],
          };
        }
      });

      const termsResults = await Promise.all(termsPromises);
      const groups = termsResults.reduce((acc, { id, terms }) => {
        acc[id] = terms;
        return acc;
      }, {});

      return {
        list: formattedList,
        groups: groups,
      };
    } catch (error) {
      throw new Error(
        "Error al cargar los atributos desde la API: " + error.message
      );
    }
  };

  const loadAttributes = useCallback(async (force = false) => {
    setIsLoadingAttributes(true);
    setErrorLoadingAttributes(null);

    try {
      const response = await fetchAttributes(force === true);
      attributes.setList(response.list);
      attributes.setGroups(response.groups);
      setIsLoadingAttributes(false);
    } catch (error) {
      console.error("Error al cargar los atributos:", error);
      setErrorLoadingAttributes(
        "No se pudieron cargar los atributos. Por favor, intenta de nuevo."
      );
      setIsLoadingAttributes(false);
      toast.error("Error al cargar los atributos: " + error.message);
    }
  }, [attributes]);

  useEffect(() => {
    if (attributes.list.length > 0) {
      setIsLoadingAttributes(false);
    } else {
      loadAttributes();
    }
  }, [attributes.list, loadAttributes]);

  const getJustOptions = () => {
    const data = {};
    attributes.selected.forEach((id) => {
      if (attributes.options[id] && Array.isArray(attributes.options[id])) {
        data[id] = attributes.options[id].map((option) => {
          const term = attributes.groups[id]?.find((i) => i.id === option);
          return term ? term.name : option;
        });
      }
    });
    attributes.setData(data);
  };

  const onFinish = (values) => {
    setloading(true);

    const data = attributes.selected
      .map((id) => {
        if (!attributes.options[id] || !Array.isArray(attributes.options[id])) {
          return null;
        }
        const options =
          attributes.groups[id]
            ?.filter((option) => attributes.options[id].includes(option.id))
            .map((option) => option.name) || [];
        return { id, options, visible: true, variation: true };
      })
      .filter(Boolean);

    getJustOptions();

    AddAttributes({ attributes: data })
      .then((res) => {
        if (res.data.id) {
          toast.success(
            data.length === 1
              ? "Se agregó correctamente el atributo"
              : "Se agregaron correctamente los atributos"
          );
          next();
          setcontrolStatusGeneral({
            ...controlStatusGeneral,
            finish: true,
          });
        } else {
          toast.error("Error al agregar el atributo");
        }
        setloading(false);
      })
      .catch((error) => {
        toast.error("Error al agregar el atributo: " + error.message);
        setloading(false);
      });
  };

  return (
    <Card
      title="Atributos y términos a utilizar"
      hoverable
      bordered
      extra={
        !isLoadingAttributes && (
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadAttributes(true)}
            size="small"
            style={{ borderRadius: "8px" }}
          >
            Actualizar términos
          </Button>
        )
      }
      style={{
        width: "100%",
        borderRadius: "16px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
        background: "#fff",
        overflow: "hidden",
        margin: "10px 0",
      }}
      headStyle={{
        borderBottom: "1px solid #f0f0f0",
        padding: "16px 24px",
        fontSize: "18px",
        fontWeight: 600,
        color: "#333",
      }}
      bodyStyle={{ padding: "24px" }}
    >
      {isLoadingAttributes ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <LoadingOutlined style={{ fontSize: "24px", color: "#1890ff" }} />
          <p style={{ marginTop: "10px", color: "#555" }}>
            Cargando atributos...
          </p>
        </div>
      ) : errorLoadingAttributes ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <p style={{ color: "#ff4d4f", marginBottom: "20px" }}>
            {errorLoadingAttributes}
          </p>
          <Button
            type="primary"
            shape="round"
            icon={<ReloadOutlined />}
            onClick={() => loadAttributes(true)}
          >
            Reintentar
          </Button>
        </div>
      ) : (
        <Form
          form={form}
          name="control-hooks"
          layout="vertical"
          onFinish={onFinish}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
            message="Elige los atributos que diferencian las variantes (por ejemplo Talla y Color)"
            description="Luego selecciona los términos que usarás. En el siguiente paso escogerás un término por atributo para crear cada variante."
          />
          <div style={{ marginBottom: "32px" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: 500,
                marginBottom: "16px",
                color: "#555",
              }}
            >
              Selección de Atributos
            </h3>
            <Row
              gutter={[24, 16]}
              style={{
                marginLeft: "-12px",
                marginRight: "-12px",
                rowGap: "0px",
              }}
            >
              <Col xs={24} lg={24}>
                <Form.Item
                  name="atributos"
                  label="Atributos"
                  rules={[
                    {
                      required: true,
                      message: "Selecciona al menos un atributo",
                    },
                  ]}
                  tooltip="Selecciona uno o varios atributos para el producto"
                >
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    placeholder="Selecciona uno o varios atributos"
                    maxTagCount="responsive"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={(values) => attributes.setSelected(values)}
                  >
                    {attributes.list.length > 0 &&
                      attributes.list.map((term) => (
                        <Option key={term.id} value={term.id}>
                          {term.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {attributes.selected.length > 0 && (
            <div style={{ marginBottom: "32px" }}>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: 500,
                  marginBottom: "16px",
                  color: "#555",
                }}
              >
                Términos de Atributos
              </h3>
              <Row
                gutter={[24, 16]}
                style={{
                  marginLeft: "-12px",
                  marginRight: "-12px",
                  rowGap: "0px",
                }}
              >
                {attributes.list.map((att) =>
                  !attributes.selected.includes(att.id) ? null : (
                    <Col xs={24} lg={12} key={att.id}>
                      <Form.Item
                        name={att.name}
                        label={att.name}
                        rules={[
                          {
                            required: true,
                            message: `Selecciona al menos un término para ${att.name}`,
                          },
                        ]}
                        tooltip={`Selecciona uno o varios términos para el atributo ${att.name}`}
                      >
                        <Select
                          mode="multiple"
                          style={{ width: "100%" }}
                          placeholder="Selecciona uno o varios términos"
                          maxTagCount="responsive"
                          allowClear
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          onChange={(values) =>
                            attributes.setOptions(att.id, values)
                          }
                        >
                          {Array.isArray(attributes.groups[att.id]) &&
                            attributes.groups[att.id].map((term) => (
                              <Option key={term.id} value={term.id}>
                                {term.name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  )
                )}
              </Row>
            </div>
          )}

          <div
            style={{
              position: "sticky",
              bottom: 0,
              zIndex: 10,
              background: "#fff",
              borderTop: "1px solid #f0f0f0",
              padding: "12px 0",
              textAlign: "center",
            }}
          >
            <Button
              type="primary"
              htmlType="submit"
              shape="round"
              size="large"
              loading={loading}
              icon={<PlusCircleFilled />}
              style={{ padding: "0 32px", height: "48px", fontWeight: 500 }}
            >
              Seleccionar y seguir
            </Button>
          </div>
        </Form>
      )}
    </Card>
  );
};

export default Atributes;
